import { isDevEnvironment } from "$routes/_lib/environment";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import { IDocumentsAccepted } from "../_lib/epocus-icons";
import {
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../_lib/epocus-palette";
import { epocusTheme } from "../_lib/epocus-theme-provider";
import { ReviewerQueueRow } from "../_lib/reviewer-queue-row";
import { useEpocusSeedData } from "../_lib/use-epocus-seed-data";
import { useReviewerQueue } from "../_lib/use-reviewer-queue";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
    gap: 20,
  },
});

const PageTitle = styled(Typography)({
  fontSize: rem(20),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const PageSubtitle = styled(Typography)({
  fontSize: rem(14),
  color: EPOCUS_TEXT_SECONDARY,
  marginTop: 4,
});

const EmptyState = styled(Box)({
  textAlign: "center",
  padding: "48px 16px",
});

const EmptyTitle = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
  marginBottom: 6,
});

const EmptyBody = styled(Typography)({
  fontSize: rem(14),
  color: EPOCUS_TEXT_SECONDARY,
});

export const EpocusReviewPage: FC = () => {
  const { seed } = useEpocusSeedData();
  const { entries, pendingCount, loading } = useReviewerQueue();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress size={32} style={{ color: EPOCUS_PRIMARY }} />
      </Box>
    );
  }

  return (
    <PageRoot>
      <Box>
        <PageTitle>Review Queue</PageTitle>
        {pendingCount > 0 && (
          <PageSubtitle>
            {pendingCount} encounter{pendingCount > 1 ? "s" : ""} pending your
            review
          </PageSubtitle>
        )}
      </Box>

      {entries.length === 0 ? (
        <EmptyState>
          <IDocumentsAccepted
            width={48}
            height={48}
            fill={EPOCUS_ICON_ACTIVE}
            aria-hidden="true"
            style={{ display: "block", margin: "0 auto 12px" }}
          />
          <EmptyTitle>No exams pending review</EmptyTitle>
          <EmptyBody>
            All caught up! New exams will appear here when they&apos;re ready.
          </EmptyBody>
          {isDevEnvironment() && (
            <Button
              variant="outlined"
              size="small"
              onClick={seed}
              style={{ marginTop: 16 }}
            >
              Seed dev data
            </Button>
          )}
        </EmptyState>
      ) : (
        <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
          {entries.map((entry) => (
            <ReviewerQueueRow key={entry.encounter.id} entry={entry} />
          ))}
        </Box>
      )}
    </PageRoot>
  );
};
